import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Save,
  Download,
  Share2,
  Sparkles,
  Brain,
  CheckCircle,
  AlertCircle,
  Lightbulb,
  Clock,
  FileText
} from 'lucide-react';
import { Essay, EssayAnalysis, EssayFeedback, EssayType } from '../../types';
import { AIService } from '../../services/aiService';
import { Button } from '../shared/Button';
import { Card } from '../shared/Card';
import { Badge } from '../shared/Badge';
import { ProgressBar } from '../shared/ProgressBar';

interface EssayEditorProps {
  essay?: Essay;
  onSave: (essay: Omit<Essay, 'id'>) => void;
  onClose: () => void;
}

const essayTypes: { value: EssayType; label: string; description: string }[] = [
  {
    value: 'personal-statement',
    label: 'Personal Statement',
    description: 'Tell your unique story and what makes you special'
  },
  {
    value: 'why-college',
    label: 'Why This College',
    description: 'Explain why you want to attend this specific school'
  },
  {
    value: 'extracurricular',
    label: 'Extracurricular Essay',
    description: 'Discuss your most meaningful activity or achievement'
  },
  {
    value: 'challenge-overcome',
    label: 'Challenge Overcome',
    description: 'Describe a challenge you faced and how you overcame it'
  },
  {
    value: 'academic-interest',
    label: 'Academic Interest',
    description: 'Explain your academic passions and career goals'
  },
  {
    value: 'supplemental',
    label: 'Supplemental Essay',
    description: 'Additional essays required by specific colleges'
  },
  {
    value: 'scholarship',
    label: 'Scholarship Essay',
    description: 'Essays for scholarship applications'
  }
];

export const EssayEditor: React.FC<EssayEditorProps> = ({ essay, onSave, onClose }) => {
  const [title, setTitle] = useState(essay?.title || '');
  const [content, setContent] = useState(essay?.content || '');
  const [type, setType] = useState<EssayType>(essay?.type || 'personal-statement');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<EssayAnalysis | null>(essay?.aiAnalysis || null);
  const [feedback, setFeedback] = useState<EssayFeedback[]>(essay?.feedback || []);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  const wordCount = content.split(' ').filter(word => word.length > 0).length;
  const charCount = content.length;

  const runAIAnalysis = useCallback(async () => {
    if (!content.trim() || wordCount < 50) return;

    setIsAnalyzing(true);
    try {
      const mockEssay: Essay = {
        id: 'temp',
        title,
        content,
        type,
        status: 'draft',
        createdDate: new Date(),
        lastModified: new Date(),
        wordCount,
        feedback: []
      };

      const [newAnalysis, newFeedback] = await Promise.all([
        AIService.analyzeEssay(mockEssay),
        AIService.generateEssayFeedback(mockEssay)
      ]);

      setAnalysis(newAnalysis);
      setFeedback(newFeedback);
    } catch (error) {
      console.error('AI analysis failed:', error);
    } finally {
      setIsAnalyzing(false);
    }
  }, [content, title, type, wordCount]);

  const handleSave = () => {
    const essayData = {
      title,
      content,
      type,
      status: 'draft' as const,
      createdDate: essay?.createdDate || new Date(),
      lastModified: new Date(),
      wordCount,
      aiAnalysis: analysis || undefined,
      feedback
    };

    onSave(essayData);
    setLastSaved(new Date());
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  const getFeedbackIcon = (type: string) => {
    switch (type) {
      case 'suggestion': return Lightbulb;
      case 'correction': return AlertCircle;
      case 'enhancement': return Sparkles;
      default: return CheckCircle;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Main Editor */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                ← Back
              </button>
              <div>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Essay Title"
                  className="text-xl font-semibold bg-transparent border-none focus:outline-none focus:ring-0 p-0"
                />
                <div className="flex items-center space-x-4 mt-1">
                  <select
                    value={type}
                    onChange={(e) => setType(e.target.value as EssayType)}
                    className="text-sm text-gray-600 bg-transparent border-none focus:outline-none"
                  >
                    {essayTypes.map(essayType => (
                      <option key={essayType.value} value={essayType.value}>
                        {essayType.label}
                      </option>
                    ))}
                  </select>
                  <span className="text-sm text-gray-500">
                    {wordCount} words • {charCount} characters
                  </span>
                  {lastSaved && (
                    <span className="text-sm text-gray-400 flex items-center">
                      <Clock size={14} className="mr-1" />
                      Saved {lastSaved.toLocaleTimeString()}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={runAIAnalysis}
                disabled={isAnalyzing || wordCount < 50}
                className="flex items-center space-x-2"
              >
                <Brain size={16} />
                <span>{isAnalyzing ? 'Analyzing...' : 'AI Analysis'}</span>
              </Button>
              <Button variant="outline" size="sm">
                <Share2 size={16} />
              </Button>
              <Button variant="outline" size="sm">
                <Download size={16} />
              </Button>
              <Button onClick={handleSave} size="sm">
                <Save size={16} className="mr-2" />
                Save
              </Button>
            </div>
          </div>
        </div>

        {/* Editor Content */}
        <div className="flex-1 p-6">
          <div className="max-w-4xl mx-auto">
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Start writing your essay here... The AI will provide real-time feedback as you write."
              className="w-full h-96 p-6 text-lg leading-relaxed bg-white rounded-xl border border-gray-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              style={{ minHeight: '500px' }}
            />

            {/* Essay Type Description */}
            <div className="mt-4 p-4 bg-primary-50 rounded-lg">
              <p className="text-sm text-primary-700">
                <strong>{essayTypes.find(t => t.value === type)?.label}:</strong>{' '}
                {essayTypes.find(t => t.value === type)?.description}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* AI Analysis Sidebar */}
      <div className="w-96 bg-white border-l border-gray-200 flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Sparkles className="w-5 h-5 mr-2 text-primary-500" />
            AI Analysis
          </h3>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {isAnalyzing && (
            <div className="text-center py-8">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full mx-auto mb-4"
              />
              <p className="text-sm text-gray-600">AI is analyzing your essay...</p>
            </div>
          )}

          {analysis && (
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                {/* Overall Score */}
                <Card padding="sm">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary-600 mb-2">
                      {analysis.overallScore}/100
                    </div>
                    <p className="text-sm text-gray-600">Overall Score</p>
                    <ProgressBar
                      progress={analysis.overallScore}
                      color="primary"
                      className="mt-3"
                    />
                  </div>
                </Card>

                {/* Detailed Scores */}
                <Card padding="sm">
                  <h4 className="font-semibold text-gray-900 mb-3">Analysis Breakdown</h4>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Structure</span>
                        <span>{analysis.structure.paragraphFlow}%</span>
                      </div>
                      <ProgressBar progress={analysis.structure.paragraphFlow} size="sm" />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Language</span>
                        <span>{analysis.language.grammar}%</span>
                      </div>
                      <ProgressBar progress={analysis.language.grammar} size="sm" color="secondary" />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Authenticity</span>
                        <span>{analysis.authenticity}%</span>
                      </div>
                      <ProgressBar progress={analysis.authenticity} size="sm" color="accent" />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Engagement</span>
                        <span>{analysis.engagement}%</span>
                      </div>
                      <ProgressBar progress={analysis.engagement} size="sm" />
                    </div>
                  </div>
                </Card>

                {/* Strengths */}
                <Card padding="sm">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <CheckCircle size={16} className="mr-2 text-green-500" />
                    Strengths
                  </h4>
                  <ul className="space-y-2">
                    {analysis.strengths.map((strength, index) => (
                      <li key={index} className="text-sm text-gray-700 flex items-start">
                        <span className="w-1.5 h-1.5 bg-green-400 rounded-full mt-2 mr-2 flex-shrink-0" />
                        {strength}
                      </li>
                    ))}
                  </ul>
                </Card>

                {/* Improvements */}
                <Card padding="sm">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <AlertCircle size={16} className="mr-2 text-orange-500" />
                    Areas for Improvement
                  </h4>
                  <ul className="space-y-2">
                    {analysis.improvements.map((improvement, index) => (
                      <li key={index} className="text-sm text-gray-700 flex items-start">
                        <span className="w-1.5 h-1.5 bg-orange-400 rounded-full mt-2 mr-2 flex-shrink-0" />
                        {improvement}
                      </li>
                    ))}
                  </ul>
                </Card>
              </motion.div>
            </AnimatePresence>
          )}

          {/* Feedback */}
          {feedback.length > 0 && (
            <Card padding="sm">
              <h4 className="font-semibold text-gray-900 mb-3">AI Suggestions</h4>
              <div className="space-y-3">
                {feedback.map((item) => {
                  const Icon = getFeedbackIcon(item.type);
                  return (
                    <div
                      key={item.id}
                      className="p-3 rounded-lg bg-gray-50 border border-gray-200"
                    >
                      <div className="flex items-start space-x-2">
                        <Icon size={16} className="mt-0.5 text-primary-500 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <Badge
                              variant={getSeverityColor(item.severity) as any}
                              size="sm"
                            >
                              {item.category}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-700 mb-2">{item.message}</p>
                          {item.suggestion && (
                            <p className="text-xs text-primary-600 bg-primary-50 p-2 rounded">
                              💡 {item.suggestion}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {!analysis && !isAnalyzing && wordCount >= 50 && (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-sm text-gray-600 mb-4">
                Get AI-powered feedback on your essay
              </p>
              <Button onClick={runAIAnalysis} size="sm">
                Analyze Essay
              </Button>
            </div>
          )}

          {wordCount < 50 && (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">
                Write at least 50 words to get AI analysis
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};